import { useAsyncRetry } from 'react-use';
import { useActiveProject } from './useActiveProject';
import { CarePlanService } from '../services/CarePlanService';
import { CODE_ACTION_PLAN, SYSTEM_ACTION_PLAN } from '../util/fhir';
import { useActiveProjectCohortId } from './useActiveProjectCohortId';
import useService from './useService';

const actionPlanService = new CarePlanService();

export const useCarePlans = (
  patientId: string,
  forceLatestCarePlan?: boolean,
) => {
  const [project] = useActiveProject();
  const [listActionPlan] = useService(actionPlanService.listCarePlans);
  const cohort = useActiveProjectCohortId();

  const state = useAsyncRetry(async (): Promise<fhir.CarePlan[]> => {
    const plans =
      (await listActionPlan(patientId, cohort, project.id)).data.entry
        ?.map(entry => entry.resource as fhir.CarePlan)
        ?.filter(resource =>
          resource?.category?.some(c =>
            c.coding?.some(
              coding =>
                coding.system === SYSTEM_ACTION_PLAN &&
                coding.code === CODE_ACTION_PLAN,
            ),
          ),
        ) ?? [];
    return plans.sort((a, b) => {
      if (forceLatestCarePlan) {
        // when forceLatestCarePlan is true, we want to sort completed care plans to the beginning of the list
        if (a.status === 'completed' && b.status !== 'completed') return -1;
        if (a.status !== 'completed' && b.status === 'completed') return 1;
      }
      // sort by active first, entered-in-error last, then by dates
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (a.status !== 'active' && b.status === 'active') return 1;
      if (a.status === 'entered-in-error' && b.status !== 'entered-in-error')
        return 1;
      if (a.status !== 'entered-in-error' && b.status === 'entered-in-error')
        return -1;
      if (!a.period?.end) return -1;
      if (!b.period?.end) return 1;
      return (
        new Date(b.period.end).getTime() - new Date(a.period.end).getTime()
      );
    });
  }, [listActionPlan, project.id, patientId, cohort]);

  return {
    carePlans: state.value,
    refetchCarePlans: state.retry,
    loading: state.loading,
  };
};
